/**
 * Pose Detection Service
 * Uses TensorFlow.js MoveNet for browser-based pose estimation
 * Completely free - runs entirely in the browser with no API costs
 */
import * as poseDetection from '@tensorflow-models/pose-detection';
import * as tf from '@tensorflow/tfjs';

let detector = null;
let isInitializing = false;

/**
 * Initialize the MoveNet pose detection model
 * Uses Lightning variant for optimal performance (30+ FPS)
 */
export const initializePoseDetection = async () => {
  if (detector) return detector;
  if (isInitializing) {
    // Wait for initialization to complete
    while (isInitializing) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return detector;
  }
  
  isInitializing = true;
  
  try {
    // Ensure TensorFlow is ready
    await tf.ready();
    console.log('TensorFlow.js backend:', tf.getBackend());
    
    // Create detector with MoveNet Lightning for lower latency
    detector = await poseDetection.createDetector(
      poseDetection.SupportedModels.MoveNet,
      {
        modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
        enableSmoothing: true,
      }
    );
    
    console.log('MoveNet pose detection initialized successfully');
    return detector;
  } catch (error) {
    console.error('Failed to initialize pose detection:', error);
    throw error;
  } finally {
    isInitializing = false;
  }
};

/**
 * Estimate poses from a video element
 * @param {HTMLVideoElement} video - The video element to analyze
 * @returns {Promise<Array>} - Array of detected poses
 */
export const estimatePose = async (video) => {
  if (!detector) {
    throw new Error('Pose detector not initialized. Call initializePoseDetection first.');
  }
  
  try {
    const poses = await detector.estimatePoses(video, {
      flipHorizontal: true, // Mirror for front camera
    });
    return poses;
  } catch (error) {
    console.error('Error estimating pose:', error);
    return [];
  }
};

/**
 * Get the current detector instance
 */
export const getDetector = () => detector;

/**
 * Clean up resources
 */
export const disposePoseDetection = async () => {
  if (detector) {
    await detector.dispose();
    detector = null;
  }
};

/**
 * Keypoint indices for body parts
 */
export const KEYPOINT_INDICES = {
  nose: 0,
  left_eye: 1,
  right_eye: 2,
  left_ear: 3,
  right_ear: 4,
  left_shoulder: 5,
  right_shoulder: 6,
  left_elbow: 7,
  right_elbow: 8,
  left_wrist: 9,
  right_wrist: 10,
  left_hip: 11,
  right_hip: 12,
  left_knee: 13,
  right_knee: 14,
  left_ankle: 15,
  right_ankle: 16,
};

/**
 * Calculate body center from keypoints
 * Uses shoulders and hips for stable center estimation
 */
export const calculateBodyCenter = (keypoints) => {
  const leftShoulder = keypoints[KEYPOINT_INDICES.left_shoulder];
  const rightShoulder = keypoints[KEYPOINT_INDICES.right_shoulder];
  const leftHip = keypoints[KEYPOINT_INDICES.left_hip];
  const rightHip = keypoints[KEYPOINT_INDICES.right_hip];
  
  const validPoints = [leftShoulder, rightShoulder, leftHip, rightHip]
    .filter(kp => kp && kp.score > 0.3);
  
  if (validPoints.length < 2) return null;
  
  const avgX = validPoints.reduce((sum, kp) => sum + kp.x, 0) / validPoints.length;
  const avgY = validPoints.reduce((sum, kp) => sum + kp.y, 0) / validPoints.length;
  const avgScore = validPoints.reduce((sum, kp) => sum + kp.score, 0) / validPoints.length;
  
  return { x: avgX, y: avgY, score: avgScore };
};

/**
 * Calculate hand positions (average of wrists)
 */
export const calculateHandPositions = (keypoints) => {
  const leftWrist = keypoints[KEYPOINT_INDICES.left_wrist];
  const rightWrist = keypoints[KEYPOINT_INDICES.right_wrist];
  
  return {
    left: leftWrist && leftWrist.score > 0.3 ? leftWrist : null,
    right: rightWrist && rightWrist.score > 0.3 ? rightWrist : null,
  };
};
